import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { BookOpen, Search, ChevronRight } from "lucide-react";
import {
  GLOSSARY,
  GLOSSARY_CATEGORIES,
  searchGlossary,
} from "@/lib/glossary";

/**
 * Full-page Glossary view.
 * - Categorised sidebar
 * - Search box
 * - Deep-linkable via /glossary#term-id
 */
export default function GlossaryPage() {
  const [query, setQuery] = useState("");
  const [activeCat, setActiveCat] = useState("all");
  const location = useLocation();

  // Scroll to anchor when arriving with a hash. Make sure search/category
  // filters don't hide the target term.
  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.replace("#", "");
    // Reset filters so the deep-linked term is always visible
    setQuery("");
    setActiveCat("all");
    // small delay so DOM is rendered after filters reset
    const t = setTimeout(() => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.classList.add("ring-2", "ring-[hsl(var(--primary))]");
        setTimeout(() => {
          el.classList.remove("ring-2", "ring-[hsl(var(--primary))]");
        }, 2400);
      }
    }, 200);
    return () => clearTimeout(t);
  }, [location.hash]);

  const filtered = useMemo(() => {
    let list = searchGlossary(query);
    if (activeCat !== "all") {
      list = list.filter((t) => t.category === activeCat);
    }
    return list;
  }, [query, activeCat]);

  const grouped = useMemo(() => {
    const map = new Map();
    GLOSSARY_CATEGORIES.forEach((c) => map.set(c.key, []));
    filtered.forEach((t) => {
      if (!map.has(t.category)) map.set(t.category, []);
      map.get(t.category).push(t);
    });
    return map;
  }, [filtered]);

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 max-w-6xl mx-auto" data-testid="glossary-page">
      <div className="mb-6">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <BookOpen className="h-4 w-4" />
          <span>Help &amp; Reference</span>
        </div>
        <h1 className="font-display text-2xl md:text-3xl font-semibold mt-2">
          Glossary
        </h1>
        <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
          Plain-English definitions of every CSE-specific and investment term used across CSE
          Investor. Use the search or jump to a category.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-6">
        {/* Sidebar */}
        <aside className="space-y-4 lg:sticky lg:top-20 self-start">
          <div className="relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              data-testid="glossary-page-search"
              placeholder="Search terms..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="pl-9"
            />
          </div>
          <Card>
            <CardHeader className="py-3">
              <CardTitle className="text-xs uppercase tracking-wider text-muted-foreground">
                Categories
              </CardTitle>
            </CardHeader>
            <CardContent className="py-2 space-y-1">
              <button
                type="button"
                onClick={() => setActiveCat("all")}
                data-testid="glossary-cat-all"
                className={`w-full text-left flex items-center justify-between px-2.5 py-1.5 rounded-md text-sm transition-colors ${
                  activeCat === "all"
                    ? "bg-accent text-accent-foreground"
                    : "text-muted-foreground hover:text-foreground hover:bg-muted"
                }`}
              >
                <span>All terms</span>
                <Badge variant="outline" className="text-[10px]">{GLOSSARY.length}</Badge>
              </button>
              {GLOSSARY_CATEGORIES.map((cat) => {
                const count = GLOSSARY.filter((t) => t.category === cat.key).length;
                return (
                  <button
                    key={cat.key}
                    type="button"
                    onClick={() => setActiveCat(cat.key)}
                    data-testid={`glossary-cat-${cat.key}`}
                    className={`w-full text-left flex items-center justify-between px-2.5 py-1.5 rounded-md text-sm transition-colors ${
                      activeCat === cat.key
                        ? "bg-accent text-accent-foreground"
                        : "text-muted-foreground hover:text-foreground hover:bg-muted"
                    }`}
                  >
                    <span>{cat.label}</span>
                    <Badge variant="outline" className="text-[10px]">{count}</Badge>
                  </button>
                );
              })}
            </CardContent>
          </Card>
        </aside>

        {/* Main Content */}
        <div className="space-y-6">
          {filtered.length === 0 ? (
            <Card>
              <CardContent className="py-16 text-center">
                <Search className="h-8 w-8 mx-auto mb-3 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">
                  No terms match &ldquo;{query}&rdquo;.
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-4"
                  onClick={() => {
                    setQuery("");
                    setActiveCat("all");
                  }}
                  data-testid="glossary-clear-filters"
                >
                  Clear filters
                </Button>
              </CardContent>
            </Card>
          ) : (
            GLOSSARY_CATEGORIES.map((cat) => {
              const terms = grouped.get(cat.key) || [];
              if (!terms.length) return null;
              return (
                <Card key={cat.key} data-testid={`glossary-page-section-${cat.key}`}>
                  <CardHeader>
                    <CardTitle className="font-display text-lg flex items-center gap-2">
                      {cat.label}
                      <Badge variant="outline" className="text-[10px]">{terms.length}</Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {terms.map((t) => (
                      <div
                        key={t.id}
                        id={t.id}
                        data-testid={`glossary-page-term-${t.id}`}
                        className="rounded-md border border-border bg-card p-4 transition-shadow"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="font-semibold text-base">{t.term}</div>
                          <a
                            href={`#${t.id}`}
                            className="text-[10px] text-muted-foreground hover:text-foreground"
                            aria-label="Permalink"
                          >
                            #
                          </a>
                        </div>
                        <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                          {t.long}
                        </p>
                        {t.aliases && t.aliases.length > 0 && (
                          <div className="mt-3 flex flex-wrap gap-1.5">
                            {t.aliases.map((a) => (
                              <Badge key={a} variant="outline" className="text-[10px]">
                                {a}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
